import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { PageHeader } from "@/components/shared";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { Link } from "react-router-dom";

const Input = (p) => <input {...p} className="px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-emerald-500 w-full" />;
const Sel = ({ children, ...p }) => <select {...p} className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white w-full">{children}</select>;
const Label = ({ children }) => <label className="text-sm font-medium text-slate-700 block mb-1">{children}</label>;

const CURRENCIES = ["USD", "CAD", "EUR", "GBP", "AUD"];
const TIMEZONES = ["America/Denver", "America/New_York", "America/Chicago", "America/Los_Angeles", "Europe/London", "Australia/Sydney"];

export default function Settings() {
  const { store, refreshStore } = useStore();
  const [f, setF] = useState(null);

  useEffect(() => { if (store) setF({ ...store }); }, [store?.id]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    try {
      await api.patch("/stores/current", {
        name: f.name, owner_name: f.owner_name, email: f.email, currency: f.currency,
        timezone: f.timezone, default_payout_pct: Number(f.default_payout_pct),
        default_period_days: Number(f.default_period_days), expiration_action: f.expiration_action,
        discount_mode: f.discount_mode,
      });
      await refreshStore(); toast.success("Settings saved");
    } catch (e) { toast.error("Failed to save"); }
  };

  if (!f) return null;

  const Section = ({ title, children }) => (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
      <h3 className="font-display font-bold text-slate-900 mb-4">{title}</h3>
      <div className="grid sm:grid-cols-2 gap-4">{children}</div>
    </div>
  );

  return (
    <div>
      <PageHeader title="Settings" subtitle="Store defaults and consignment rules">
        <button data-testid="save-settings-btn" onClick={save} className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold px-4 py-2 rounded-lg text-sm">
          <Save className="w-4 h-4" /> Save Changes
        </button>
      </PageHeader>

      <div className="space-y-5 max-w-3xl">
        <Section title="Store Information">
          <div><Label>Store name</Label><Input data-testid="set-name" value={f.name || ""} onChange={(e) => set("name", e.target.value)} /></div>
          <div><Label>Owner / manager</Label><Input data-testid="set-owner" value={f.owner_name || ""} onChange={(e) => set("owner_name", e.target.value)} /></div>
          <div><Label>Email</Label><Input data-testid="set-email" value={f.email || ""} onChange={(e) => set("email", e.target.value)} /></div>
          <div><Label>Currency</Label><Sel data-testid="set-currency" value={f.currency} onChange={(e) => set("currency", e.target.value)}>{CURRENCIES.map((c) => <option key={c}>{c}</option>)}</Sel></div>
          <div><Label>Time zone</Label><Sel data-testid="set-timezone" value={f.timezone} onChange={(e) => set("timezone", e.target.value)}>{TIMEZONES.map((t) => <option key={t}>{t}</option>)}</Sel></div>
        </Section>

        <Section title="Consignment Rules">
          <div><Label>Default payout %</Label><Input data-testid="set-payout" type="number" value={f.default_payout_pct} onChange={(e) => set("default_payout_pct", e.target.value)} /></div>
          <div><Label>Default period (days)</Label><Input data-testid="set-period" type="number" value={f.default_period_days} onChange={(e) => set("default_period_days", e.target.value)} /></div>
          <div><Label>On expiration</Label><Sel data-testid="set-exp-action" value={f.expiration_action} onChange={(e) => set("expiration_action", e.target.value)}>
            {["return", "donate", "markdown", "extend", "custom"].map((a) => <option key={a} value={a} className="capitalize">{a}</option>)}</Sel></div>
          <div><Label>Discount handling</Label><Sel data-testid="set-discount-mode" value={f.discount_mode} onChange={(e) => set("discount_mode", e.target.value)}>
            <option value="reduces_consignor">Discount reduces consignor share</option><option value="store_absorbs">Store absorbs discount</option></Sel></div>
        </Section>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h3 className="font-display font-bold text-slate-900 mb-2">Subscription</h3>
          <p className="text-sm text-slate-500 mb-3">Status: <span className="font-semibold text-emerald-700" data-testid="settings-subscription-status">{f.subscription_status || "inactive"}</span></p>
          <Link to="/billing" data-testid="settings-billing-link" className="text-sm font-semibold text-emerald-700 underline">Manage subscription on the Billing page</Link>
        </div>
      </div>
    </div>
  );
}
